import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquareText, X, Send, ChevronLeft, Users, UserCircle, Maximize2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useChatWidgetsHidden } from "@/hooks/useChatWidgetsHidden";
import { Input, Button } from "@/components/ui-kit";

/**
 * Iter v34k (#4) — Karyawan-to-Karyawan chat popout for karyawan portal.
 * Mirrors ChatCSPopout but targets /karyawan-chat/* endpoints.
 *
 * Floats a bubble bottom-right. Open → list of threads (DM + Group). Click thread → inline chat.
 * Polls every 6s when open; passive every 30s when collapsed (for unread count).
 */
export default function KaryawanChatPopout() {
  const { user } = useAuth();
  const isKaryawan = (user?.account_type || "").toLowerCase() === "karyawan";
  const { hidden: chatHidden } = useChatWidgetsHidden();
  const enabled = !!user && isKaryawan && !chatHidden;

  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const totalUnread = useMemo(() => threads.reduce((s, t) => s + (t.unread_count || 0), 0), [threads]);

  const loadThreads = useCallback(async () => {
    if (!enabled) return;
    try { const r = await api.get("/karyawan-chat/threads"); setThreads(r.data); }
    catch { /* ignore */ }
  }, [enabled]);

  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const r = await api.get(`/karyawan-chat/threads/${tid}/messages`);
      setMessages(r.data);
      api.post(`/karyawan-chat/threads/${tid}/read`).then(loadThreads).catch(() => {});
    } catch { /* ignore */ }
  }, [loadThreads]);

  useEffect(() => { if (enabled) loadThreads(); }, [enabled, loadThreads]);
  useEffect(() => { if (open && activeId) loadMessages(activeId); }, [open, activeId, loadMessages]);

  // Polling
  useEffect(() => {
    if (!enabled) return undefined;
    const fast = open ? 6000 : 30000;
    const id = setInterval(() => {
      loadThreads();
      if (open && activeId) loadMessages(activeId);
    }, fast);
    return () => clearInterval(id);
  }, [enabled, open, activeId, loadThreads, loadMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const me = (user?.email || "").toLowerCase();
  const activeThread = threads.find((t) => t.id === activeId);

  const send = async () => {
    if (!input.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/karyawan-chat/threads/${activeId}/messages`, { body: input.trim() });
      setInput("");
      loadMessages(activeId);
      loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim"); }
    finally { setSending(false); }
  };

  // Iter v34w (#3) — Soft-delete per-sisi.
  const deleteThreadSelf = async (tid) => {
    if (!window.confirm("Hapus chat ini dari daftar Anda? (Lawan tetap melihat percakapan)")) return;
    try {
      await api.delete(`/karyawan-chat/threads/${tid}`);
      toast.success("Chat dihapus dari daftar Anda");
      setActiveId(null);
      loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus chat"); }
  };

  if (!enabled) return null;

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <>
      {/* Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 bg-terra-600 hover:bg-terra-700 text-white rounded-full p-3.5 shadow-lg flex items-center gap-2"
          data-testid="karyawan-chat-popout-bubble"
          title="Chat Karyawan"
        >
          <MessageSquareText className="h-5 w-5" />
          {totalUnread > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-[10px] rounded-full px-1.5 py-0.5">{totalUnread > 99 ? "99+" : totalUnread}</span>}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-4 right-4 z-40 w-[92vw] max-w-sm bg-white shadow-2xl border border-stone-200 flex flex-col overflow-hidden" style={{ height: "70vh", maxHeight: "560px" }} data-testid="karyawan-chat-popout">
          {/* Header */}
          <div className="bg-stone-900 text-white px-3 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              {activeId && (
                <button onClick={() => setActiveId(null)} className="hover:text-terra-200" title="Kembali"><ChevronLeft className="h-4 w-4" /></button>
              )}
              <MessageSquareText className="h-4 w-4" />
              <span className="text-sm font-semibold truncate">{activeThread ? activeThread.display_name : "Chat Karyawan"}</span>
            </div>
            <div className="flex items-center gap-1">
              {activeId && (
                <button onClick={() => deleteThreadSelf(activeId)} className="hover:text-red-300" title="Hapus chat ini" data-testid="karyawan-chat-popout-delete"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
              <Link to="/karyawan/chat-teman" className="hover:text-terra-200" title="Buka di halaman penuh" data-testid="karyawan-chat-popout-fullpage"><Maximize2 className="h-3.5 w-3.5" /></Link>
              <button onClick={() => setOpen(false)} className="hover:text-terra-200" data-testid="karyawan-chat-popout-close"><X className="h-4 w-4" /></button>
            </div>
          </div>

          {/* Body */}
          {!activeId ? (
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 ? (
                <div className="text-center text-xs text-stone-400 p-8">
                  Belum ada chat. Klik tombol di bawah untuk mulai.
                </div>
              ) : threads.map((t) => (
                <button key={t.id}
                  onClick={() => setActiveId(t.id)}
                  className="w-full text-left p-3 border-b border-stone-100 hover:bg-stone-50"
                  data-testid={`karyawan-chat-popout-thread-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {t.kind === "group" ? <Users className="h-3.5 w-3.5 text-terra-600 shrink-0" /> : <UserCircle className="h-3.5 w-3.5 text-stone-600 shrink-0" />}
                      <span className="font-medium text-sm text-stone-900 truncate">{t.display_name}</span>
                    </div>
                    {t.unread_count > 0 && <span className="bg-terra-500 text-white text-[10px] rounded-full px-2 py-0.5">{t.unread_count}</span>}
                  </div>
                  <p className="text-[11px] text-stone-500 mt-1 truncate">{t.last_message_preview || "(belum ada pesan)"}</p>
                </button>
              ))}
              <div className="p-3 border-t border-stone-100 text-center">
                <Link to="/karyawan/chat-teman" className="text-xs text-terra-600 hover:text-terra-800 underline" data-testid="karyawan-chat-popout-newlink">+ Buat chat baru / kelola</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50/30">
                {messages.map((m) => {
                  const isMe = (m.sender_email || "").toLowerCase() === me;
                  return (
                    <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] p-2 ${isMe ? "bg-terra-500 text-white" : "bg-white border border-stone-200 text-stone-800"}`}>
                        {!isMe && activeThread?.kind === "group" && <p className="text-[10px] font-semibold text-terra-700 mb-0.5">{m.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[9px] mt-1 text-right ${isMe ? "text-white/70" : "text-stone-400"}`}>{fmtTime(m.ts)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-stone-200 p-2 flex items-center gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder="Ketik…" className="flex-1 !text-sm" data-testid="karyawan-chat-popout-input" />
                <Button onClick={send} disabled={sending || !input.trim()} className="!px-2 !py-1.5" data-testid="karyawan-chat-popout-send"><Send className="h-3.5 w-3.5" /></Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
